// What the document overlay was opened *on* (PRD-0031 slice 08).
//
// The star of the *Photo matching* tab and of the identity page opens the
// overlay on one candidate: a face, its page, and the identity that candidate
// was suggested for. All three travel in the URL next to `?doc=`, so the focused
// view is as shareable as the plain one — and pure functions read and write them,
// so the rule is testable without a router.

/** URL search param that holds the face the overlay emphasises (PRD-0031). */
export const FACE_PARAM = 'face'
/** The page that face sits on, so a PDF opens there before the faces arrive. */
export const FACE_PAGE_PARAM = 'facePage'
/** The identity the focused face is compared against. */
export const FOCUS_PERSON_PARAM = 'focusPerson'
/** Play the focused face's pass once the recording opens (PRD-0074 slice 04). */
export const PLAY_PARAM = 'play'
/** Open a recording at a second of it, and run to another: a scene or an appearance found by
 *  search (PRD-0074 slice 08). Whole seconds in the URL. */
/** Where to open a recording and where to stop, in whole seconds — named for the overlay, as
 *  `facePage` is: the HUMINT recording page owns the bare `?t=` of its own player. */
export const TIME_PARAM = 'docAt'
export const TIME_END_PARAM = 'docUntil'

/** What a caller asks the overlay to open, focus included (slice 08). */
export interface DocumentOverlayTarget {
  documentId: string
  /** The page of the face, so a PDF opens there straight away. */
  page?: number | null
  /** The face to select and emphasise on the photo. */
  faceId?: string | null
  /** The identity the focused face is compared against — the star's identity. */
  focusPersonId?: string | null
  /** Play the face's pass as soon as the recording's player is up — "Play pass" from a card. */
  play?: boolean
  /** Open the recording at this second, and run it to `untilSeconds` (or on) — a search hit. */
  atSeconds?: number | null
  untilSeconds?: number | null
}

/** The face, the page and the identity the overlay opened on. */
export interface DocumentFocus {
  /** The face to select, null when the overlay was opened the ordinary way. */
  faceId: string | null
  /** The page that face sits on, null for an image or an unfocused open. */
  page: number | null
  /** The identity to compare the focused face against, null without a focus. */
  personId: string | null
  /** Whether the focused face's pass is to be played when the recording opens. */
  play: boolean
  /** The span of the recording to play on open, null when none was asked for. */
  span: { from: number; to: number | null } | null
}

/** No focus at all — what the documents list, search and the agent open. */
export const NO_FOCUS: DocumentFocus = {
  faceId: null,
  page: null,
  personId: null,
  play: false,
  span: null,
}

/** Every focus key, in one place: what a close clears and a write rewrites. */
const FOCUS_PARAMS = [
  FACE_PARAM,
  FACE_PAGE_PARAM,
  FOCUS_PERSON_PARAM,
  PLAY_PARAM,
  TIME_PARAM,
  TIME_END_PARAM,
] as const

/** Leave no focus behind: on an ordinary close, and before a target's own is written. */
export function clearFocusParams(params: URLSearchParams): void {
  for (const key of FOCUS_PARAMS) params.delete(key)
}

/** A page or a second survives the URL only as plain digits, so a hand-edited
 *  link cannot put a NaN or a fraction into the layer. */
function readWhole(raw: string | null | undefined): number | null {
  return raw != null && /^\d+$/.test(raw) ? Number.parseInt(raw, 10) : null
}

/** The params as the URL spells them, before they mean anything. */
export interface RawFocusParams {
  face: string | null
  facePage: string | null
  focusPerson: string | null
  /** `'1'` to play the face's pass; absent or anything else not to. */
  play?: string | null
  /** Whole seconds: where to open a recording, and where to stop. */
  t?: string | null
  tEnd?: string | null
}

/**
 * The focus the URL carries. A page or an identity without a face is no focus:
 * the face is what the overlay selects, and the rest only describes it. A moment
 * of a recording is a focus of its own, with or without a face.
 *
 * Takes the raw strings rather than the `URLSearchParams`, so the provider can
 * memoise on them — a fresh focus object on every render would re-render every
 * consumer of the context.
 */
export function focusFromParams(raw: RawFocusParams): DocumentFocus {
  const from = readWhole(raw.t)
  const span = from === null ? null : { from, to: readWhole(raw.tEnd) }
  if (!raw.face) return span ? { ...NO_FOCUS, span } : NO_FOCUS
  return {
    faceId: raw.face,
    page: readWhole(raw.facePage),
    personId: raw.focusPerson || null,
    play: raw.play === '1',
    span,
  }
}

/**
 * Write a target's focus into the params, clearing what it does not carry — so
 * an ordinary `open()` from the documents list leaves no focus of a previous one
 * behind.
 */
export function writeFocusParams(params: URLSearchParams, target: DocumentOverlayTarget): void {
  const from = wholeSeconds(target.atSeconds, Math.floor)
  const span = from === null ? null : { from, to: wholeSeconds(target.untilSeconds, Math.ceil) }
  const focus: DocumentFocus = target.faceId
    ? {
        faceId: target.faceId,
        page: target.page ?? null,
        personId: target.focusPersonId ?? null,
        play: target.play === true,
        span,
      }
    : { ...NO_FOCUS, span }
  const written: Array<[string, string | null]> = [
    [FACE_PARAM, focus.faceId],
    [FACE_PAGE_PARAM, focus.page === null ? null : String(focus.page)],
    [FOCUS_PERSON_PARAM, focus.personId],
    [PLAY_PARAM, focus.play ? '1' : null],
    [TIME_PARAM, focus.span ? String(focus.span.from) : null],
    [TIME_END_PARAM, focus.span?.to != null ? String(focus.span.to) : null],
  ]
  clearFocusParams(params)
  for (const [key, value] of written) {
    if (value !== null) params.set(key, value)
  }
}

/** A moment of a recording goes into the URL as whole seconds; a time that is no time is none. */
function wholeSeconds(
  value: number | null | undefined,
  round: (seconds: number) => number
): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? round(value) : null
}
